import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/client';
import { SearchBar } from '@/components/ui';

export function useFarmsAndRoles() {
  const farms = useQuery({ queryKey: ['farms'], queryFn: () => api.get('/api/v1/farms') });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get('/api/v1/roles') });
  return { farms: farms.data ?? [], roles: roles.data ?? [] };
}

export interface Scope {
  allFarms: boolean;
  farmIds: string[];
}

/**
 * Which farms a person may work in — shared by Add user, Farms and the bulk
 * "Set farms". A farm's stores follow it, including stores added later.
 */
export function FarmAssignment({
  allFarms,
  farmIds,
  onChange,
  suggestAll = false,
}: Scope & {
  onChange: (next: Scope) => void;
  /** A chosen role covers the whole company, so "All farms" is likely meant. */
  suggestAll?: boolean;
}) {
  const { farms } = useFarmsAndRoles();
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? farms.filter((f) => f.code.toLowerCase().includes(needle) || f.nameEn.toLowerCase().includes(needle))
    : farms;

  function toggle(id: string) {
    onChange({
      allFarms: false,
      farmIds: farmIds.includes(id) ? farmIds.filter((f) => f !== id) : [...farmIds, id],
    });
  }

  return (
    <div className="settings__control">
      <span className="field__label">Farms this person may work in</span>

      {farms.length > 8 && (
        <div data-unsaved-ignore>
          <SearchBar value={search} onChange={setSearch} placeholder="Find a farm…" />
        </div>
      )}

      <div className="settings__choices">
        <button
          type="button"
          className={`chip ${allFarms ? 'chip--on' : ''}`}
          aria-pressed={allFarms}
          onClick={() => onChange({ allFarms: true, farmIds: [] })}
        >
          {allFarms ? '✓ ' : ''}All farms
        </button>
        {shown.map((f) => {
          const on = !allFarms && farmIds.includes(f.id);
          return (
            <button
              key={f.id}
              type="button"
              className={`chip ${on ? 'chip--on' : ''}`}
              aria-pressed={on}
              onClick={() => toggle(f.id)}
            >
              {on ? '✓ ' : ''}
              {f.nameEn} ({f.code})
            </button>
          );
        })}
      </div>

      <p className="settings__hint">
        {allFarms
          ? 'Covers farms added later as well, which a fixed list would not.'
          : farmIds.length === 0
            ? suggestAll
              ? 'A role you chose covers the whole company, so All farms is probably what you want.'
              : 'Pick at least one farm, or choose All farms. Someone with no farm can see nothing.'
            : `${farmIds.length} farm${farmIds.length === 1 ? '' : 's'} chosen. Their stores follow automatically, including new ones.`}
      </p>
    </div>
  );
}
